import { useQuery, type UseQueryResult } from '@tanstack/react-query';
import { useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, Navigate, useParams } from 'react-router';
import type { McpGroup } from '@/api/types';
import { isSettingsTab, paths, type SettingsTab } from '@/app/paths';
import { useSession } from '@/app/session';
import { Avatar, Button, Card, Icon, Pill, Tag, type IconName, type Tone } from '@/design-system';
import { cx } from '@/lib/cx';
import { formatMonthYear, formatNumber, formatSum } from '@/lib/format';
import { OperatorDrawer } from '@/features/domains/OperatorDrawer';
import { operatorQueueQuery } from '@/features/domains/api';
import { useBreadcrumbs } from '@/shell/breadcrumbs';
import { LoadError } from '@/shell/LoadError';
import { NoAccess, PageHeader } from '@/shell/PageHeader';
import type { User } from '@/api/types';
import { AiEditDrawer } from './AiEditDrawer';
import { DomainsEditDrawer } from './DomainsEditDrawer';
import { OpenAiDrawer, openAiRoutes, routeKey } from './OpenAiDrawer';
import { aiQuery, domainsQuery, mcpQuery, rulesQuery, storageQuery, usersQuery } from './api';
import { CatalogSources } from './CatalogSources';
import { ResearchSources } from './ResearchSources';
import { AddUserModal, GRANTABLE, UserAccessModal } from './UserModals';
import s from './settings.module.css';

const TABS: { key: SettingsTab; icon: IconName }[] = [
  { key: 'ai', icon: 'bot' },
  { key: 'mcp', icon: 'plug-zap' },
  { key: 'storage', icon: 'hard-drive' },
  { key: 'domains', icon: 'globe' },
  { key: 'rules', icon: 'shield' },
  { key: 'users', icon: 'users' },
  // Additive (P3-E04, DESIGN_SPEC §9 C7): the catalog sources and the research connectors.
  { key: 'catalogs', icon: 'boxes' },
  { key: 'sources', icon: 'scan-search' },
];

type Tv = (k: string, o?: Record<string, unknown>) => string;

/** /settings/:tab — Admin only; a PM sees NoAccess under the same breadcrumbs (DESIGN_SPEC §4.11). */
export function SettingsRoute() {
  const { t } = useTranslation('settings');
  const { tab } = useParams();
  const { isAdmin } = useSession();
  useBreadcrumbs([{ label: t('title') }]);
  if (!isSettingsTab(tab)) return <Navigate to={paths.settings('ai')} replace />;
  return isAdmin ? <SettingsPage tab={tab} /> : <NoAccess />;
}

function SettingsPage({ tab }: { tab: SettingsTab }) {
  const { t } = useTranslation('settings');
  return (
    <>
      <PageHeader eyebrow={t('eyebrow')} title={t('title')} />
      <div className={s.layout}>
        <nav className={s.tabs} aria-label={t('tabs_label')}>
          {TABS.map((x) => (
            <Link key={x.key} to={paths.settings(x.key)} className={cx(s.tab, tab === x.key && s.active)} aria-current={tab === x.key ? 'page' : undefined}>
              <Icon name={x.icon} size={16} />
              {t(`tabs.${x.key}`)}
            </Link>
          ))}
        </nav>
        <div className={s.content}>
          {tab === 'ai' && <AiProviders />}
          {tab === 'mcp' && <McpTools />}
          {tab === 'storage' && <Storage />}
          {tab === 'domains' && <DomainProviders />}
          {tab === 'rules' && <SystemRules />}
          {tab === 'users' && <Users />}
          {tab === 'catalogs' && <CatalogSources />}
          {tab === 'sources' && <ResearchSources />}
        </div>
      </div>
    </>
  );
}

function Loaded<T>({ q, children }: { q: UseQueryResult<T>; children: (data: T) => ReactNode }) {
  if (q.isError) return <LoadError error={q.error} onRetry={() => void q.refetch()} />;
  if (!q.data) return null;
  return <>{children(q.data)}</>;
}

function AiProviders() {
  const { t } = useTranslation('settings');
  const tv = t as unknown as Tv;
  const [editing, setEditing] = useState(false);
  const [openai, setOpenai] = useState(false);
  return (
    <Loaded q={useQuery(aiQuery)}>
      {(d) => {
        const routes = openAiRoutes(d.values);
        const openaiKey = (d.secrets as Record<string, { set: boolean; masked: string | null } | undefined>).openai_api_key;
        // The second provider shows as a card once it has a key or a route (P8-A03); before that, the design's dashed card.
        const secondary = routes.length > 0 || !!openaiKey?.set;
        return (
          <>
            <div className={s.grid280}>
              {/* One provider in P1, so it is the primary one (a second adapter is P2). */}
              <Card texture="accent" relative className={s.provider}>
                <div className={s.rowBetween}>
                  <span className={s.name17}>{tv(`ai.providers.${d.values.provider}`, { defaultValue: d.values.provider })}</span>
                  <Pill tone="ok" size="tagTight">
                    {t('ai.primary')}
                  </Pill>
                </div>
                <dl className={s.kv}>
                  <div className={s.kvRow}>
                    <dt className={s.kvKey}>{t('ai.adapter')}</dt>
                    <dd className={s.kvValue}>{tv(`ai.adapters.${d.values.adapter}`, { defaultValue: d.values.adapter })}</dd>
                  </div>
                  <div className={s.kvRow}>
                    <dt className={s.kvKey}>{t('ai.api_key')}</dt>
                    {/* Secrets are write-only: the server only ever says {set, masked}. */}
                    <dd className={cx(s.kvValue, s.mono)}>{d.secrets.api_key?.set ? (d.secrets.api_key.masked ?? '••••') : t('ai.key_not_set')}</dd>
                  </div>
                  <div className={s.kvRow}>
                    <dt className={s.kvKey}>{t('ai.limit')}</dt>
                    <dd className={s.kvValue}>{t('ai.limit_value', { amount: formatNumber(d.values.org_monthly_budget_usd) })}</dd>
                  </div>
                  <div className={s.kvRow}>
                    <dt className={s.kvKey}>{t('ai.parallel')}</dt>
                    <dd className={s.kvValue}>{d.values.parallel_jobs}</dd>
                  </div>
                </dl>
              </Card>
              {secondary ? (
                <Card relative className={s.provider} data-testid="openai-card">
                  <div className={s.rowBetween}>
                    <span className={s.name17}>{t('ai.providers.openai')}</span>
                    <Pill tone="mute" size="tagTight">
                      {t('ai.secondary')}
                    </Pill>
                  </div>
                  <dl className={s.kv}>
                    <div className={s.kvRow}>
                      <dt className={s.kvKey}>{t('ai.model')}</dt>
                      <dd className={cx(s.kvValue, s.mono)}>{d.values.openai_model}</dd>
                    </div>
                    <div className={s.kvRow}>
                      <dt className={s.kvKey}>{t('ai.api_key')}</dt>
                      <dd className={cx(s.kvValue, s.mono)}>{openaiKey?.set ? (openaiKey.masked ?? '••••') : t('ai.key_not_set')}</dd>
                    </div>
                    <div className={s.kvRow}>
                      <dt className={s.kvKey}>{t('ai.routes')}</dt>
                      <dd className={s.kvValue}>{routes.length ? routes.map((r) => tv(`ai.route_names.${routeKey(r)}`)).join(', ') : t('ai.routes_none')}</dd>
                    </div>
                  </dl>
                  <Button variant="secondary" size={36} className={s.alignStart} onClick={() => setOpenai(true)}>
                    {t('ai.configure')}
                  </Button>
                </Card>
              ) : (
                <Card texture="dashed" className={s.extra}>
                  <span className={s.title15}>{t('ai.extra_title')}</span>
                  <span className={s.text125}>{t('ai.extra_text')}</span>
                  <Button variant="secondary" size={36} className={s.alignStart} onClick={() => setOpenai(true)}>
                    {t('ai.add')}
                  </Button>
                </Card>
              )}
            </div>
            {/* Editing is additive (P1-F04): the design shows the provider read-only. */}
            <Button variant="secondary" size={38} icon="pencil" iconSize={14} className={s.alignStart} onClick={() => setEditing(true)}>
              {t('ai_edit.open')}
            </Button>
            {editing && <AiEditDrawer data={d} onClose={() => setEditing(false)} />}
            {openai && <OpenAiDrawer data={d} onClose={() => setOpenai(false)} />}
          </>
        );
      }}
    </Loaded>
  );
}

const ACCESS_TONE: Record<McpGroup['mode'], Tone> = {
  read: 'ok',
  isolated: 'warn',
  sandbox: 'ok',
  needs_confirmation: 'err',
};

function McpTools() {
  const { t } = useTranslation('settings');
  const tv = t as unknown as Tv;
  return (
    <Loaded q={useQuery(mcpQuery)}>
      {(d) => (
        <Card padding={8}>
          <div className={s.intro}>{t('mcp.intro')}</div>
          {d.values.groups.map((g) => {
            const note = g.group === 'workspace_write' || g.group === 'publishing' ? t(`mcp.notes.${g.group}`) : null;
            return (
              <div key={g.group} className={s.mcpRow}>
                <span className={s.mcpName}>{tv(`mcp.groups.${g.group}`, { defaultValue: g.group })}</span>
                <span className={s.mcpTools}>{g.tools.join(', ') + (note ? ` — ${note}` : '')}</span>
                <Pill tone={ACCESS_TONE[g.mode] ?? 'mute'} size="tag">
                  {tv(`mcp.access.${g.mode}`, { defaultValue: g.mode })}
                </Pill>
              </div>
            );
          })}
        </Card>
      )}
    </Loaded>
  );
}

function Storage() {
  const { t } = useTranslation('settings');
  return (
    <Loaded q={useQuery(storageQuery)}>
      {(d) => (
        <div className={s.grid200}>
          <Card className={s.store}>
            <span className={s.label12}>{t('storage.object')}</span>
            <span className={s.value18}>{d.values.object_storage}</span>
            <span className={s.monoSub}>{d.values.bucket_label}</span>
          </Card>
          <Card className={s.store}>
            <span className={s.label12}>{t('storage.cdn')}</span>
            <span className={s.value18}>{d.values.cdn}</span>
            <span className={s.monoSub}>{t('storage.cdn_rules', { html: d.values.html_cache, assets: d.values.assets_cache })}</span>
          </Card>
          <Card className={s.store}>
            <span className={s.label12}>{t('storage.prefixes')}</span>
            <span className={s.value18}>{(d.values.prefixes ?? []).join(' · ')}</span>
            {/* Cache keys are always tenant-aware (ARCHITECTURE §6): a platform rule, not a setting. */}
            <span className={s.monoSub}>{t('storage.tenant_keys')}</span>
          </Card>
        </div>
      )}
    </Loaded>
  );
}

const REGISTRAR_TONE: Record<string, Tone> = { api_check: 'warn', active: 'ok', disabled: 'mute' };

/** «Проверка API» until the registrar is configured with its key; «Работает» once ready; «Выключен» without one. */
function registrarStatus(registrar: string, ready: boolean): 'api_check' | 'active' | 'disabled' {
  if (registrar === 'none') return 'disabled';
  return ready ? 'active' : 'api_check';
}

function DomainProviders() {
  const { t } = useTranslation('settings');
  const tv = t as unknown as Tv;
  const [editing, setEditing] = useState(false);
  const [operator, setOperator] = useState(false);
  // P7-B02: the orders waiting for the operator (the assisted ahost.uz path, or nobody could confirm them).
  const waiting = useQuery(operatorQueueQuery).data?.length ?? 0;
  return (
    <Loaded q={useQuery(domainsQuery)}>
      {(d) => {
        const status = registrarStatus(d.values.registrar, !!d.ready.registrar);
        const until = d.values.wildcard_tls_until;
        return (
          <>
          <Card className={s.registrar}>
            <div className={s.rowBetween}>
              <span className={s.name17}>{tv(`domains.registrars.${d.values.registrar}`, { defaultValue: d.values.registrar })}</span>
              <Pill tone={REGISTRAR_TONE[status] ?? 'mute'} size="tagTight">
                {t(`domains.status.${status}`)}
              </Pill>
            </div>
            <span className={s.note}>{t('domains.note', { budget: formatSum(d.values.pm_monthly_budget_uzs, true) })}</span>
            <div className={s.chips}>
              <Tag soft>{t('domains.demo_zone', { zone: d.values.demo_zone ?? t('domains.not_set') })}</Tag>
              <Tag soft>{t('domains.wildcard_tls', { until: until ? formatMonthYear(until.slice(0, 7)) : t('domains.not_set') })}</Tag>
            </div>
          </Card>
          {/* P7-B01 (additive, as «Изменить настройки AI»): the registrar, its key, the PM budget and the price table;
              P7-B02 (additive, on the same row): the operator's queue. */}
          <div className={s.buttonRow}>
            <Button variant="secondary" size={38} icon="pencil" iconSize={14} onClick={() => setEditing(true)}>
              {t('domains_edit.open')}
            </Button>
            <Button variant="secondary" size={38} icon="list-checks" iconSize={14} onClick={() => setOperator(true)}>
              {waiting ? t('domains.operator_open_count', { count: waiting }) : t('domains.operator_open')}
            </Button>
          </div>
          {editing && <DomainsEditDrawer data={d} onClose={() => setEditing(false)} />}
          {operator && <OperatorDrawer onClose={() => setOperator(false)} />}
          </>
        );
      }}
    </Loaded>
  );
}

function SystemRules() {
  const { t } = useTranslation('settings');
  const tv = t as unknown as Tv;
  return (
    <Loaded q={useQuery(rulesQuery)}>
      {(d) => {
        // The platform's fixed rules (PLAN §3), then the editable one: what a demo does after production.
        const rows = [...(d.fixed ?? []).map((r) => ({ key: r.key, value: r.value })), { key: 'demo_after_production', value: d.values.demo_after_production }];
        return (
          <Card padding={0} className={s.rules}>
            {rows.map((r) => (
              <div key={r.key} className={s.rule}>
                <span>{tv(`rules.names.${r.key}`, { defaultValue: r.key })}</span>
                <span className={s.ruleValue}>{tv(`rules.values.${r.value}`, { defaultValue: r.value })}</span>
              </div>
            ))}
          </Card>
        );
      }}
    </Loaded>
  );
}

function Users() {
  const { t } = useTranslation('settings');
  const tv = t as unknown as Tv;
  const [adding, setAdding] = useState(false);
  const [access, setAccess] = useState<User | null>(null);
  return (
    <Loaded q={useQuery(usersQuery)}>
      {(users) => (
        <>
          <Card padding={8}>
            {users.map((u) => (
              <div
                key={u.id}
                className={cx(s.user, s.userClickable)}
                role="button"
                tabIndex={0}
                aria-label={`${t('users_edit.open')}: ${u.full_name}`}
                onClick={() => setAccess(u)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    setAccess(u);
                  }
                }}
              >
                <Avatar name={u.full_name} size={38} />
                <div className={s.userText}>
                  <span className={s.userName}>{u.full_name}</span>
                  <span className={s.userRole}>{u.role === 'admin' ? t('users.role.admin') : u.is_lead_pm ? t('users.role.pm_lead') : t('users.role.pm')}</span>
                </div>
                {/* P6-D02 (additive, C10): the scopes an Admin granted this PM. */}
                {u.role === 'pm' &&
                  u.extra_scopes
                    .filter((x) => (GRANTABLE as readonly string[]).includes(x))
                    .map((x) => (
                      <Pill key={x} tone="acc" size="tagMd" title={t('users.extra_title')}>
                        {tv(`users.extra.${x.replace(':', '__')}`)}
                      </Pill>
                    ))}
                <Pill tone={u.mfa_enabled ? 'ok' : 'warn'} size="tagMd">
                  {u.mfa_enabled ? t('users.mfa_on') : t('users.mfa_off')}
                </Pill>
              </div>
            ))}
            <div className={s.rbac}>{t('users.note')}</div>
          </Card>
          {/* Creating PMs and granting scopes are additive (P1-F04). */}
          <Button variant="secondary" size={38} icon="plus" iconSize={15} className={s.alignStart} onClick={() => setAdding(true)}>
            {t('users_edit.add')}
          </Button>
          {adding && <AddUserModal onClose={() => setAdding(false)} />}
          {access && <UserAccessModal user={access} onClose={() => setAccess(null)} />}
        </>
      )}
    </Loaded>
  );
}
